import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from '../lib/axios';

const SAVE_FLAG_KEY = 'storeAdminSaveCreds';
const SAVED_USERNAME_KEY = 'storeAdminSavedUsername';
const LEGACY_SAVED_PASSWORD_KEY = 'storeAdminSavedPw';

export default function StoreAdminLoginPage() {
  const [form, setForm] = useState({ username: '', pw: '' });
  const [error, setError] = useState('');
  const [statusHint, setStatusHint] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const [saveCreds, setSaveCreds] = useState(() => {
    try { return localStorage.getItem(SAVE_FLAG_KEY) === 'true'; } catch { return false; }
  });

  useEffect(() => {
    try {
      const saved = localStorage.getItem(SAVE_FLAG_KEY) === 'true';
      localStorage.removeItem(LEGACY_SAVED_PASSWORD_KEY);
      if (!saved) return;
      const username = localStorage.getItem(SAVED_USERNAME_KEY) || '';
      setForm((prev) => ({ ...prev, username, pw: '' }));
    } catch {}
  }, []);

  const canSubmit = useMemo(
    () => form.username.trim().length > 0 && (form.pw || '').length > 0,
    [form.username, form.pw]
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit || loading) return;
    setError('');
    setStatusHint('');
    setLoading(true);
    try {
      await axios.post('/store-admin/login', form);
      try {
        localStorage.setItem(SAVE_FLAG_KEY, String(saveCreds));
        if (saveCreds) localStorage.setItem(SAVED_USERNAME_KEY, form.username || '');
        else localStorage.removeItem(SAVED_USERNAME_KEY);
        localStorage.removeItem(LEGACY_SAVED_PASSWORD_KEY);
      } catch {}
      navigate('/store-admin');
    } catch (err) {
      const code = err?.response?.data?.error;
      const status = err?.response?.data?.status;
      if (code === 'not_approved') {
        if (status === 'pending') setStatusHint('현재 승인 대기 중이야. 상위관리자 승인 후 로그인할 수 있어.');
        else if (status === 'rejected') setStatusHint('가입이 거절됐어. 상위관리자에게 문의해줘.');
        else if (status === 'disabled') setStatusHint('이 계정은 비활성화됐어. 상위관리자에게 문의해줘.');
        else setStatusHint('아직 승인되지 않은 계정이야.');
      } else if (code === 'invalid_credentials') {
        setError('로그인 실패. 아이디나 비밀번호를 다시 확인해줘.');
      } else {
        setError('로그인에 실패했어. 잠시 후 다시 시도해줘.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="admin-scope dark flex min-h-screen items-center justify-center"
      style={{ background: '#07090e' }}
    >
      {/* Ambient violet glow */}
      <div
        className="pointer-events-none fixed inset-0"
        style={{ background: 'radial-gradient(ellipse 60% 40% at 50% 0%, rgba(130,98,161,0.06) 0%, transparent 70%)' }}
      />

      <div className="relative w-full max-w-sm px-6">
        {/* Logo mark */}
        <div className="mb-8 flex flex-col items-center gap-3">
          <div
            className="flex h-12 w-12 items-center justify-center rounded-2xl"
            style={{ background: '#0d1522', boxShadow: '0 0 0 1px rgba(130,98,161,0.20), 0 0 24px rgba(130,98,161,0.08)' }}
          >
            <span className="font-mono text-lg font-bold" style={{ color: '#8262A1' }}>B</span>
          </div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-600">BOROO LIQUIDS · STORE</p>
        </div>

        {/* Card */}
        <div
          className="overflow-hidden rounded-2xl"
          style={{ background: '#0d1522', boxShadow: '0 0 0 1px rgba(255,255,255,0.07), 0 32px 64px rgba(0,0,0,0.5)' }}
        >
          <div className="px-7 py-7">
            <h1 className="text-xl font-semibold text-slate-100">매장 관리자 로그인</h1>
            <p className="mt-1 text-sm text-slate-500">승인 완료된 계정만 로그인할 수 있어.</p>

            <form className="mt-6 space-y-4" onSubmit={handleSubmit}>
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-slate-500" htmlFor="store-id">아이디</label>
                <input
                  id="store-id"
                  type="text"
                  placeholder="아이디"
                  value={form.username}
                  onChange={(e) => setForm({ ...form, username: e.target.value })}
                  autoComplete="username"
                  autoFocus
                  className="h-10 w-full rounded-lg bg-white/[0.05] px-3 text-sm text-slate-100 outline-none ring-1 ring-white/[0.08] placeholder:text-slate-700 focus:ring-[#8262A1]/50 transition-all"
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-medium text-slate-500" htmlFor="store-pw">비밀번호</label>
                <input
                  id="store-pw"
                  type="password"
                  placeholder="••••••••"
                  value={form.pw}
                  onChange={(e) => setForm({ ...form, pw: e.target.value })}
                  autoComplete="current-password"
                  className="h-10 w-full rounded-lg bg-white/[0.05] px-3 text-sm text-slate-100 outline-none ring-1 ring-white/[0.08] placeholder:text-slate-700 focus:ring-[#8262A1]/50 transition-all"
                />
              </div>

              <label className="flex items-center gap-2 text-xs text-slate-500 select-none cursor-pointer">
                <input
                  type="checkbox"
                  checked={saveCreds}
                  onChange={(e) => {
                    const v = e.target.checked;
                    setSaveCreds(v);
                    try {
                      localStorage.setItem(SAVE_FLAG_KEY, String(v));
                      if (!v) localStorage.removeItem(SAVED_USERNAME_KEY);
                      localStorage.removeItem(LEGACY_SAVED_PASSWORD_KEY);
                    } catch {}
                  }}
                  className="accent-[#8262A1]"
                />
                아이디 저장
              </label>

              {statusHint && <p className="text-xs text-amber-400">{statusHint}</p>}
              {error && <p className="text-xs text-rose-400">{error}</p>}

              <button
                type="submit"
                disabled={!canSubmit || loading}
                className="mt-1 h-10 w-full rounded-lg text-sm font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ background: '#8262A1', color: '#ffffff' }}
              >
                {loading ? '로그인 중…' : '로그인'}
              </button>
            </form>
          </div>

          <div
            className="flex items-center justify-between px-7 py-4"
            style={{ borderTop: '1px solid rgba(255,255,255,0.05)' }}
          >
            <Link to="/store-admin/signup" className="text-xs text-slate-600 transition-colors hover:text-slate-400">
              회원가입(승인요청)
            </Link>
            <Link to="/admin/login" className="text-xs text-slate-600 transition-colors hover:text-slate-400">
              상위관리자 로그인
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
